/**
 * Coerce POST /api/events JSON quirks before Zod (also used client-side before fetch).
 * Keeps persistence-friendly strings (YYYY-MM-DD, trimmed text, numeric lat/lng).
 */
export function normalizeCreateEventApiInputForZod(input: unknown): unknown {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    return input
  }
  const o = { ...(input as Record<string, unknown>) }

  const trimIfString = (k: string) => {
    const v = o[k]
    if (typeof v === "string") o[k] = v.trim()
  }

  for (const k of [
    "title",
    "description",
    "date",
    "locationName",
    "address",
    "eventTimeZone",
    "image",
    "cfImageId",
  ]) {
    trimIfString(k)
  }

  for (const k of ["locationName", "address"] as const) {
    const v = o[k]
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const L = v as Record<string, unknown>
      o[k] = String(L.label ?? L.name ?? L.displayName ?? "").trim()
    }
  }

  const loc = o.location
  if (loc && typeof loc === "object" && !Array.isArray(loc)) {
    const L = loc as Record<string, unknown>
    o.location = String(
      L.label ?? L.name ?? L.displayName ?? L.locationName ?? L.value ?? "",
    ).trim()
  } else if (typeof loc === "string") {
    o.location = loc.trim()
  }

  if ("time" in o) {
    const t = o.time
    if (t === null || t === undefined) {
      delete o.time
    } else if (typeof t === "string") {
      const tr = t.trim()
      if (!tr) delete o.time
      else o.time = tr
    } else {
      delete o.time
    }
  }

  for (const coord of ["lat", "lng"] as const) {
    if (!(coord in o)) continue
    const v = o[coord]
    if (v === null || v === undefined) continue
    if (typeof v === "string") {
      const n = Number(v)
      if (Number.isFinite(n)) o[coord] = n
    }
  }

  if (typeof o.date === "string") {
    const d = o.date.trim()
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(d)
    if (m) o.date = m[1]!
  }

  if (
    o.recurrence &&
    typeof o.recurrence === "object" &&
    !Array.isArray(o.recurrence)
  ) {
    const r = { ...(o.recurrence as Record<string, unknown>) }
    if (typeof r.untilDateLocal === "string") {
      const u = r.untilDateLocal.trim()
      if (!u) {
        delete r.untilDateLocal
      } else {
        const um = /^(\d{4}-\d{2}-\d{2})/.exec(u)
        r.untilDateLocal = um ? um[1]! : u
      }
    }
    o.recurrence = r
  }

  if (Array.isArray(o.links)) {
    o.links = o.links.map((link) => {
      if (!link || typeof link !== "object" || Array.isArray(link)) return link
      const L = { ...(link as Record<string, unknown>) }
      if (typeof L.id === "string") L.id = L.id.trim()
      if (typeof L.label === "string") L.label = L.label.trim()
      if (typeof L.url === "string") L.url = L.url.trim()
      return L
    })
  }

  if (Array.isArray(o.contributions)) {
    o.contributions = o.contributions.map((c) => {
      if (!c || typeof c !== "object" || Array.isArray(c)) return c
      const C = { ...(c as Record<string, unknown>) }
      if (typeof C.id === "string") C.id = C.id.trim()
      if (typeof C.handle === "string") C.handle = C.handle.trim()
      return C
    })
  }

  return o
}
